import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, AlertCircle, Package, ChevronDown } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPOSIT_PRICING = [
  { key: "1d", label: "1 giorno / 1 day", price: 25 },
  { key: "2d", label: "2 giorni / 2 days", price: 45 },
  { key: "3d", label: "3 giorni / 3 days", price: 60 },
  { key: "4d", label: "4 giorni / 4 days", price: 75 },
  { key: "5d", label: "5 giorni / 5 days", price: 85 },
  { key: "6-7d", label: "6/7 giorni / days", price: 110 },
  { key: "8d", label: "8 giorni / 8 days", price: 130 },
  { key: "9d", label: "9 giorni / 9 days", price: 145 },
  { key: "10d", label: "10 giorni / 10 days", price: 160 },
  { key: "11d", label: "11 giorni / 11 days", price: 175 },
  { key: "12d", label: "12 giorni / 12 days", price: 190 },
  { key: "13d", label: "13 giorni / 13 days", price: 200 },
  { key: "14d", label: "14 giorni / 14 days", price: 210 },
  { key: "15d", label: "15 giorni / 15 days", price: 220 },
  { key: "16d", label: "16 giorni / 16 days", price: 230 },
  { key: "17d", label: "17 giorni / 17 days", price: 240 },
  { key: "18d", label: "18 giorni / 18 days", price: 250 },
  { key: "19d", label: "19 giorni / 19 days", price: 260 },
  { key: "20d", label: "20 giorni / 20 days", price: 270 },
  { key: "21d", label: "21 giorni / 21 days", price: 280 },
  { key: "4w", label: "4 settimane / 4 weeks", price: 345 },
  { key: "1m", label: "1 mese / 1 month", price: 360 },
  { key: "2m", label: "2 mesi / 2 months", price: 590 },
  { key: "3m", label: "3 mesi / 3 months", price: 720 },
  { key: "season", label: "Stagionale / Seasonal", price: 800 },
  { key: "winter", label: "Invernale (al mese) / Winter (per month)", price: 100 },
];

const translations = {
  it: {
    title: "Modulo Deposito",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Tesseramento",
    step2: "Dati Personali",
    step3: "Deposito & Prezzo",
    memberQuestion: "Sei tesserato/a quest'anno?",
    yes: "Si, sono tesserato/a",
    no: "No, non sono tesserato/a",
    name: "Nome",
    surname: "Cognome",
    phone: "Cellulare",
    email: "Email",
    selectDeposit: "Seleziona la durata del deposito",
    depositNote: "Il prezzo base include 2 tavole e 2 vele. Ogni pezzo extra: +50%",
    kiteNote: "Kitesurf bassa stagione (01/09-01/08): -20%",
    next: "Avanti",
    back: "Indietro",
    submit: "Invia Richiesta",
    success: "Richiesta deposito inviata!",
    successDesc: "Il team ti contatterà per confermare la disponibilità.",
    error: "Errore nell'invio",
    close: "Chiudi",
    required: "Campo obbligatorio",
    selectedPrice: "Prezzo selezionato",
    fromDate: "Da quando",
    toDate: "A quando",
  },
  en: {
    title: "Deposit Form",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Membership",
    step2: "Personal Info",
    step3: "Deposit & Price",
    memberQuestion: "Are you a member this year?",
    yes: "Yes, I'm a member",
    no: "No, I'm not a member",
    name: "Name",
    surname: "Surname",
    phone: "Phone",
    email: "Email",
    selectDeposit: "Select deposit duration",
    depositNote: "Base price includes 2 boards and 2 sails. Each extra piece: +50%",
    kiteNote: "Kitesurf low season (01/09-01/08): -20%",
    next: "Next",
    back: "Back",
    submit: "Submit Request",
    success: "Deposit request submitted!",
    successDesc: "The team will contact you to confirm availability.",
    error: "Error submitting",
    close: "Close",
    required: "Required field",
    selectedPrice: "Selected price",
    fromDate: "From",
    toDate: "To",
  },
  fr: {
    title: "Formulaire de Dépôt",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Adhésion",
    step2: "Données Personnelles",
    step3: "Dépôt & Prix",
    memberQuestion: "Êtes-vous membre cette année?",
    yes: "Oui, je suis membre",
    no: "Non, je ne suis pas membre",
    name: "Prénom",
    surname: "Nom",
    phone: "Téléphone",
    email: "Email",
    selectDeposit: "Sélectionnez la durée du dépôt",
    depositNote: "Le prix de base comprend 2 planches et 2 voiles. Chaque pièce supplémentaire: +50%",
    kiteNote: "Kitesurf basse saison (01/09-01/08): -20%",
    next: "Suivant",
    back: "Retour",
    submit: "Envoyer la demande",
    success: "Demande de dépôt envoyée!",
    successDesc: "L'équipe vous contactera pour confirmer la disponibilité.",
    error: "Erreur lors de l'envoi",
    close: "Fermer",
    required: "Champ obligatoire",
    selectedPrice: "Prix sélectionné",
    fromDate: "Du",
    toDate: "Au",
  },
  de: {
    title: "Lagerformular",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Mitgliedschaft",
    step2: "Persönliche Daten",
    step3: "Lager & Preis",
    memberQuestion: "Sind Sie dieses Jahr Mitglied?",
    yes: "Ja, ich bin Mitglied",
    no: "Nein, ich bin kein Mitglied",
    name: "Vorname",
    surname: "Nachname",
    phone: "Telefon",
    email: "E-Mail",
    selectDeposit: "Lagerdauer wählen",
    depositNote: "Der Grundpreis beinhaltet 2 Boards und 2 Segel. Jedes weitere Stück: +50%",
    kiteNote: "Kitesurf Nebensaison (01/09-01/08): -20%",
    next: "Weiter",
    back: "Zurück",
    submit: "Anfrage senden",
    success: "Lageranfrage gesendet!",
    successDesc: "Das Team wird Sie kontaktieren um die Verfügbarkeit zu bestätigen.",
    error: "Fehler beim Senden",
    close: "Schließen",
    required: "Pflichtfeld",
    selectedPrice: "Ausgewählter Preis",
    fromDate: "Von",
    toDate: "Bis",
  },
  es: {
    title: "Formulario de Depósito",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Membresía",
    step2: "Datos Personales",
    step3: "Depósito & Precio",
    memberQuestion: "¿Eres miembro este año?",
    yes: "Sí, soy miembro",
    no: "No, no soy miembro",
    name: "Nombre",
    surname: "Apellido",
    phone: "Teléfono",
    email: "Email",
    selectDeposit: "Seleccionar duración del depósito",
    depositNote: "El precio base incluye 2 tablas y 2 velas. Cada pieza extra: +50%",
    kiteNote: "Kitesurf temporada baja (01/09-01/08): -20%",
    next: "Siguiente",
    back: "Atrás",
    submit: "Enviar solicitud",
    success: "¡Solicitud de depósito enviada!",
    successDesc: "El equipo te contactará para confirmar la disponibilidad.",
    error: "Error al enviar",
    close: "Cerrar",
    required: "Campo obligatorio",
    selectedPrice: "Precio seleccionado",
    fromDate: "Desde",
    toDate: "Hasta",
  },
};

export const DepositForm = ({ isOpen, onClose, language = "it", sessionId = "" }) => {
  const t = translations[language] || translations.it;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [formData, setFormData] = useState({
    is_member: null,
    name: "", surname: "", phone: "", email: "",
    deposit_option: "", deposit_label: "", deposit_price: 0,
    start_date: "", end_date: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectDeposit = (opt) => {
    setFormData(prev => ({
      ...prev,
      deposit_option: opt.key,
      deposit_label: opt.label,
      deposit_price: opt.price,
    }));
    setDropdownOpen(false);
  };

  const canGoNext = () => {
    if (step === 1) return formData.is_member !== null;
    if (step === 2) return formData.name && formData.surname && formData.phone;
    if (step === 3) return formData.deposit_option && formData.start_date && formData.end_date;
    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_URL}/api/deposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          is_member: formData.is_member === true,
          session_id: sessionId,
        }),
      });
      if (!res.ok) throw new Error("Server error");
      setSubmitSuccess(true);
    } catch {
      setSubmitError(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative w-full sm:max-w-lg max-h-[92vh] bg-gradient-to-b from-slate-50 to-white rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                <div>
                  <h2 className="font-bold text-lg" data-testid="deposit-form-title">{t.title}</h2>
                  <p className="text-xs text-white/80">{t.subtitle}</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="deposit-form-close" className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-1 mt-3">
              {[1, 2, 3].map(i => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-white" : "bg-white/30"}`} />
              ))}
            </div>
            <p className="text-xs text-white/70 mt-1">{t[`step${step}`]} ({step}/3)</p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {submitSuccess ? (
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">{t.success}</h3>
                <p className="text-slate-500 text-sm">{t.successDesc}</p>
                <button onClick={onClose} data-testid="deposit-close-success" className="mt-4 px-6 py-2.5 bg-emerald-500 text-white rounded-full font-medium hover:bg-emerald-600 transition-colors">
                  {t.close}
                </button>
              </motion.div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -30, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {/* Step 1: Membership */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-slate-700 text-center">{t.memberQuestion}</h3>
                      <div className="grid grid-cols-1 gap-3">
                        <button
                          type="button"
                          data-testid="deposit-member-yes"
                          onClick={() => updateField("is_member", true)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${formData.is_member === true ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.is_member === true ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                              {formData.is_member === true && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <span className="font-semibold text-slate-700">{t.yes}</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          data-testid="deposit-member-no"
                          onClick={() => updateField("is_member", false)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${formData.is_member === false ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.is_member === false ? "border-orange-500 bg-orange-500" : "border-slate-300"}`}>
                              {formData.is_member === false && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <span className="font-semibold text-slate-700">{t.no}</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Personal Info */}
                  {step === 2 && (
                    <div className="space-y-3">
                      {[
                        { label: t.name + " *", field: "name", type: "text", placeholder: "Mario" },
                        { label: t.surname + " *", field: "surname", type: "text", placeholder: "Rossi" },
                        { label: t.phone + " *", field: "phone", type: "tel", placeholder: "+39 333 1234567" },
                        { label: t.email, field: "email", type: "email", placeholder: "mario@email.com" },
                      ].map(({ label, field, type, placeholder }) => (
                        <div key={field} className="space-y-1">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
                          <input
                            data-testid={`deposit-${field}`}
                            type={type}
                            value={formData[field]}
                            onChange={(e) => updateField(field, e.target.value)}
                            placeholder={placeholder}
                            className="w-full bg-white/80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent outline-none transition-all"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Step 3: Deposit pricing + dates */}
                  {step === 3 && (
                    <div className="space-y-4">
                      {/* Date range */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t.fromDate} *</label>
                          <input
                            data-testid="deposit-start-date"
                            type="date"
                            value={formData.start_date}
                            onChange={(e) => updateField("start_date", e.target.value)}
                            className="w-full bg-white/80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent outline-none transition-all"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t.toDate} *</label>
                          <input
                            data-testid="deposit-end-date"
                            type="date"
                            value={formData.end_date}
                            onChange={(e) => updateField("end_date", e.target.value)}
                            className="w-full bg-white/80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent outline-none transition-all"
                          />
                        </div>
                      </div>

                      <h3 className="text-sm font-semibold text-slate-600">{t.selectDeposit}</h3>

                      {/* Dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          data-testid="deposit-pricing-trigger"
                          onClick={() => setDropdownOpen(!dropdownOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${formData.deposit_option ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <span className={`text-sm ${formData.deposit_option ? "font-semibold text-emerald-700" : "text-slate-400"}`}>
                            {formData.deposit_option ? `${formData.deposit_label} — ${formData.deposit_price}€` : t.selectDeposit + "..."}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                        </button>

                        <AnimatePresence>
                          {dropdownOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden mt-1 border border-slate-200 rounded-xl bg-white shadow-lg"
                            >
                              <div className="max-h-56 overflow-y-auto p-1">
                                {DEPOSIT_PRICING.map(opt => (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    data-testid={`deposit-opt-${opt.key}`}
                                    onClick={() => selectDeposit(opt)}
                                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors mb-0.5 flex items-center justify-between ${formData.deposit_option === opt.key ? "bg-emerald-50 font-semibold text-emerald-700 ring-1 ring-emerald-300" : "hover:bg-slate-50"}`}
                                  >
                                    <span>{opt.label}</span>
                                    <span className={`font-bold ${formData.deposit_option === opt.key ? "text-emerald-700" : "text-slate-700"}`}>{opt.price}€</span>
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {formData.deposit_price > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center" data-testid="deposit-total">
                          <p className="text-xs text-emerald-600 font-medium">{t.selectedPrice}</p>
                          <p className="text-2xl font-bold text-emerald-700">{formData.deposit_price}€</p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs text-amber-700">{t.depositNote}</p>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                          <p className="text-xs text-sky-700">{t.kiteNote}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

            {submitError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {!submitSuccess && (
            <div className="flex-shrink-0 px-5 py-3 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} data-testid="deposit-form-back" className="flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> {t.back}
                </button>
              ) : <div />}

              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canGoNext()}
                  data-testid="deposit-form-next"
                  className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-full hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t.next} <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canGoNext() || isSubmitting}
                  data-testid="deposit-form-submit"
                  className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-green-500 rounded-full hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? "..." : t.submit} <Check className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DepositForm;
